import React, { useCallback, useEffect, useRef, useState } from 'react';

import '../../teacher_payments/teacher_payments.css';
import { is_permission_error } from '../../teacher_payments/format';
import { fetch_audit_list } from '../../teacher_payments/audit/api';
import DataTable from '../../teacher_payments/shared/data_table';
import LoadFailed from '../../teacher_payments/shared/load_failed';
import { Note, What, Who } from '../../teacher_payments/shared/cells';
import {
  SOURCES,
  append_source,
  history_sources,
  list_params,
  merged_history
} from './change_history_logic';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import {
  CHANGE_HISTORY_CSV_COLUMNS,
  change_history_csv_loader,
  change_history_csv_name
} from './change_history_csv';

// Two tracks (list-row pattern): when and who · what changed, with the reason as a note under it.
const COLUMNS = [
  {
    key: 'when',
    role: 'identifier',
    // The time carries its own IST: cards show no header.
    label: 'When',
    track: { compact: '30%', full: '26%' },
    render: (r) => <Who name={r.when} meta={[r.who]} />
  },
  {
    key: 'changes',
    label: 'Change',
    render: (r) => (
      <What title={r.changes[0] || '—'} lines={r.changes.slice(1)}>
        {r.reason ? <Note label="Reason" text={r.reason} /> : null}
      </What>
    )
  }
];

const EMPTY = Object.fromEntries(SOURCES.map((s) => [s.key, { rows: [], next_before_id: null }]));

/*
 * every edit made from the Payment details cards for this teacher, newest first. Grant-only like
 * the cards: a 401/403 hides the section.
 */
const ChangeHistory = ({ teacher_uuid, version = 0, onboarding_only = false }) => {
  const sources_list = history_sources(onboarding_only);
  const [sources, setSources] = useState(EMPTY);
  const [state, setState] = useState('loading'); // loading | ready | error | forbidden
  const [more_busy, setMoreBusy] = useState(false);
  const [more_error, setMoreError] = useState('');
  // Only the latest request may land: a save-triggered reload must not be overwritten by an older one.
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!teacher_uuid) return;
    seq.current += 1;
    const mine = seq.current;
    setState('loading');
    setMoreError('');
    Promise.all(sources_list.map((s) => fetch_audit_list(list_params(teacher_uuid, s))))
      .then((pages) => {
        if (mine !== seq.current) return;
        const next = {};
        sources_list.forEach((s, i) => {
          next[s.key] = append_source(null, pages[i]);
        });
        setSources(next);
        setState('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        if (is_permission_error(e)) {
          setState('forbidden');
          return;
        }
        console.error('payment change history load failed', e);
        setState('error');
      });
  }, [teacher_uuid, onboarding_only]);

  useEffect(() => {
    setSources(EMPTY);
    load();
  }, [load, version]);

  const load_more = () => {
    const pending = sources_list.filter((s) => sources[s.key].next_before_id);
    if (!pending.length || more_busy) return;
    const mine = seq.current;
    setMoreBusy(true);
    setMoreError('');
    Promise.all(
      pending.map((s) =>
        fetch_audit_list(list_params(teacher_uuid, s, sources[s.key].next_before_id))
      )
    )
      .then((pages) => {
        if (mine !== seq.current) return;
        setSources((prev) => {
          const next = { ...prev };
          pending.forEach((s, i) => {
            next[s.key] = append_source(prev[s.key], pages[i]);
          });
          return next;
        });
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        console.error('payment change history load more failed', e);
        setMoreError('Could not load older changes.');
      })
      .finally(() => {
        if (mine === seq.current) setMoreBusy(false);
      });
  };

  if (!teacher_uuid || state === 'forbidden') return null;

  const { rows, has_more } = merged_history(sources);

  let body;
  if (state === 'error') {
    body = <LoadFailed text="Could not load the change history." on_retry={load} />;
  } else if (state === 'loading') {
    body = <div className="tp-empty">Loading change history…</div>;
  } else {
    body = (
      <>
        <DataTable
          columns={COLUMNS}
          rows={rows}
          rowKey={(r) => r.id}
          emptyText="No changes have been made from this page yet."
          card_below={560}
        />
        {has_more ? (
          <div className="tp-frow tp-ch-more">
            <button type="button" className="tp-btn-out" onClick={load_more} disabled={more_busy}>
              {more_busy ? 'Loading…' : 'Show older changes'}
            </button>
            {more_error ? <span className="tp-err">{more_error}</span> : null}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <section className="tp-card" aria-label="Change history">
      <div className="tp-card-hd">
        <span className="tp-card-hd-main">
          <span className="tp-card-hd-title">Change history</span>
          <span className="tp-card-hd-sub">
            GST, Free Pass, profile live date and base rate edits, newest first
          </span>
        </span>
        <span className="tp-card-hd-acts">
          <DownloadCsvButton
            columns={CHANGE_HISTORY_CSV_COLUMNS}
            load_rows={change_history_csv_loader(fetch_audit_list, teacher_uuid, sources_list)}
            file_name={() => change_history_csv_name(teacher_uuid)}
            disabled={state !== 'ready'}
          />
          <button
            type="button"
            className="tp-btn-out"
            onClick={load}
            disabled={state === 'loading'}
          >
            Refresh
          </button>
        </span>
      </div>
      {body}
    </section>
  );
};

export default ChangeHistory;
